import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  Method,
  MeetingItemStatus,
  RiskIndicator,
  Session,
  SessionStatus,
  Task,
  TaskStatus,
} from "../../../models";
import type { MeetingItemRepresentation } from "../../../models/meetingItemRepresentation";
import { SessionService } from "../../../services/coachingNavigator/sessionService";
import { MethodService } from "../../../services/coachingNavigator/methodService";
import { TaskService } from "../../../services/coachingNavigator/taskService";
import { CoacheeService } from "../../../services/coachingNavigator/coacheeService";
import {
  MethodFormDialog,
  MethodFormValues,
} from "../../../components/coachingNavigator/createMethodPanel";
import AddTask from "../../../components/coachingNavigator/addTask";
import { ItemEditingPanelSimple } from "../../../components/itemEditingPanelSimple";
import { Context } from "../../../components/context";
import config from "../../../config";
import {
  SessionInfoSection,
  SessionContentSection,
  SessionTasksSection,
} from "./index";
import SkeletonLoader from "../../../components/skeletonLoader";
import { AttachmentsModal } from "../../../components/attachmentsModal";

const sessionService = new SessionService(config.api.baseUrl, "/sessions");
const methodService = new MethodService(config.api.baseUrl, "/method");
const taskService = new TaskService(config.api.baseUrl, "/coachingtasks");
const coacheeService = new CoacheeService(config.api.baseUrl, "/coachee");

const SessionDetails = () => {
  const appContext = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { t } = useTranslation();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [allSessionIds, setAllSessionIds] = useState<string[]>([]);

  // Editable fields
  const [clientObjective, setClientObjective] = useState("");
  const [keyInsights, setKeyInsights] = useState("");
  const [sessionRemarks, setSessionRemarks] = useState("");
  const [sessionNotes, setSessionNotes] = useState("");
  const [methods, setMethods] = useState<string[]>([]);
  const [availableMethods, setAvailableMethods] = useState<Method[]>([]);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [methodDialogOpen, setMethodDialogOpen] = useState(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTaskItem, setEditingTaskItem] = useState<
    MeetingItemRepresentation | undefined
  >(undefined);
  const [showSessionAttachments, setShowSessionAttachments] = useState(false);
  const [taskAttachmentsTarget, setTaskAttachmentsTarget] = useState<Task | null>(null);
  const [coacheeName, setCoacheeName] = useState("");
  const [coacheeEmail, setCoacheeEmail] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const clientObjectiveRef = useRef("");
  const keyInsightsRef = useRef("");
  const sessionRemarksRef = useRef("");
  const sessionNotesRef = useRef("");
  const methodsRef = useRef<string[]>([]);
  const saveQueueRef = useRef<Promise<Session | null>>(Promise.resolve(null));

  const handleClientObjectiveChange = (value: string) => {
    clientObjectiveRef.current = value;
    setClientObjective(value);
  };

  const handleKeyInsightsChange = (value: string) => {
    keyInsightsRef.current = value;
    setKeyInsights(value);
  };

  const handleSessionRemarksChange = (value: string) => {
    sessionRemarksRef.current = value;
    setSessionRemarks(value);
  };

  const handleSessionNotesChange = (value: string) => {
    sessionNotesRef.current = value;
    setSessionNotes(value);
  };

  const handleMethodsChange = (value: string[]) => {
    methodsRef.current = value;
    setMethods(value);
  };

  const creatorEmail = useMemo(
    () => (appContext.state.invoker?.email || "").trim(),
    [appContext.state.invoker?.email],
  );

  const loadSession = async (id: string) => {
    setLoading(true);
    try {
      const data = await sessionService.get(id);
      setSession({ ...data, attachments: (data as any)?.attachments ?? 0 });
      handleClientObjectiveChange(data.objective || "");
      handleKeyInsightsChange(data.keyInsights || "");
      handleSessionRemarksChange((data.remarks && data.remarks.join("\n")) || "");
      handleSessionNotesChange((data.notes && data.notes.join("\n")) || "");
      handleMethodsChange(data.methodsUsed || []);
    } catch (err) {
      console.error("Failed to load session", err);
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = async (sessionIdValue: string) => {
    setTasksLoading(true);
    try {
      const data = await taskService.getBySession(sessionIdValue);
      const list = Array.isArray(data) ? data : [];
      const normalized = list.map((task) => ({ ...task, attachments: task.attachments ?? 0 }));
      setTasks(normalized);
    } catch (err) {
      console.error("Failed to load tasks", err);
      setTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
      setEditing(false);
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (session?.id) {
      loadTasks(session.id);
    }
  }, [session?.id]);

  useEffect(() => {
    let isActive = true;

    if (!creatorEmail) {
      setAllSessionIds([]);
      return;
    }

    const load = async () => {
      try {
        const data = await sessionService.getByCreator(creatorEmail);
        if (!isActive) return;
        const clientId = session?.clientId;
        const filtered = (data || []).filter(
          (s) => s.id && (!clientId || s.clientId === clientId),
        );
        const sorted = filtered.sort((a, b) => {
          const aTime = a.startTime ? new Date(a.startTime).getTime() : 0;
          const bTime = b.startTime ? new Date(b.startTime).getTime() : 0;
          return aTime - bTime;
        });
        setAllSessionIds(sorted.map((s) => s.id as string));
      } catch (err) {
        console.error("Failed to load sessions index", err);
      }
    };

    const loadName = async (clientId: string) => {
      try {
        const data = await coacheeService.get(clientId);
        if (!isActive) return;
        setCoacheeName(data?.name || "");
        setCoacheeEmail(data?.email || "");
      } catch (err) {
        console.error("Failed to load coachee", err);
        if (isActive) {
          setCoacheeName("");
          setCoacheeEmail("");
        }
      }
    };

    load();
    if (session?.clientId) {
      loadName(session.clientId);
    }

    return () => {
      isActive = false;
    };
  }, [creatorEmail, session?.clientId]);

  useEffect(() => {
    let isActive = true;
    setMethodsLoading(true);
    const load = async () => {
      try {
        if (!creatorEmail) {
          if (isActive) setAvailableMethods([]);
          return;
        }
        const data = await methodService.getByCreator(creatorEmail);
        if (!isActive) return;
        const list = Array.isArray(data) ? data : [];
        setAvailableMethods(list);
      } catch (err) {
        console.error("Failed to load methods", err);
      } finally {
        if (isActive) setMethodsLoading(false);
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [creatorEmail]);

  const currentIndex = allSessionIds.indexOf(sessionId || "");
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < allSessionIds.length - 1;
  const isCompleted = session?.status === SessionStatus.Completed;
  const readOnly = isCompleted && !editing;
  const locationState = location.state as { returnTo?: string } | null;
  const returnTo = locationState?.returnTo;
  const detailsNavigationState = returnTo ? { state: { returnTo } } : undefined;

  const deriveStatus = (
    currentStatus: SessionStatus | undefined,
    nextMethods?: string[],
    sessionCandidate: Session | null = session,
  ): SessionStatus => {
    if (!sessionCandidate) return currentStatus || SessionStatus.Planned;
    if (currentStatus === SessionStatus.Completed) {
      return SessionStatus.Completed;
    }

    const methodsToUse = nextMethods ?? methodsRef.current;
    const hasContent = Boolean(
      clientObjectiveRef.current.trim() ||
      keyInsightsRef.current.trim() ||
      sessionRemarksRef.current.trim() ||
      sessionNotesRef.current.trim() ||
      methodsToUse.length,
    );

    const end = sessionCandidate.endTime
      ? new Date(sessionCandidate.endTime)
      : undefined;
    const isEnded = end ? end.getTime() < Date.now() : false;

    if (!hasContent) return SessionStatus.Planned;
    return isEnded ? SessionStatus.Draft : SessionStatus.Prepared;
  };

  const buildSessionPayload = (
    sessionToSave: Session,
    nextMethods?: string[],
  ): Session => {
    const methodsUsed = nextMethods ?? methodsRef.current;
    return {
      ...sessionToSave,
      objective: clientObjectiveRef.current.trim() || undefined,
      keyInsights: keyInsightsRef.current.trim() || undefined,
      remarks: sessionRemarksRef.current.trim()
        ? sessionRemarksRef.current.split(/\r?\n/).filter(Boolean)
        : undefined,
      notes: sessionNotesRef.current.trim()
        ? sessionNotesRef.current.split(/\r?\n/).filter(Boolean)
        : undefined,
      methodsUsed,
      status: deriveStatus(sessionToSave.status, methodsUsed, sessionToSave),
    };
  };

  const queueSave = (sessionToSave: Session, nextMethods?: string[]) => {
    saveQueueRef.current = saveQueueRef.current
      .catch(() => null)
      .then(async () => {
        setSaving(true);
        try {
          const payload = buildSessionPayload(sessionToSave, nextMethods);
          const saved = await sessionService.save(payload);
          setSession(saved);
          return saved;
        } catch (err) {
          console.error("Failed to save session", err);
          return null;
        } finally {
          setSaving(false);
        }
      });

    return saveQueueRef.current;
  };

  const navigateToPrevious = () => {
    if (!hasPrevious) return;
    navigate(
      `/sessions/${allSessionIds[currentIndex - 1]}`,
      detailsNavigationState,
    );
  };

  const navigateToNext = () => {
    if (!hasNext) return;
    navigate(
      `/sessions/${allSessionIds[currentIndex + 1]}`,
      detailsNavigationState,
    );
  };

  const handleSave = async (
    sessionOverride?: Session,
    nextMethods?: string[],
  ): Promise<Session | null> => {
    const sessionToSave =
      sessionOverride &&
      typeof sessionOverride === "object" &&
      typeof sessionOverride.id === "string"
        ? sessionOverride
        : session;
    if (!sessionToSave?.id) return null;
    return queueSave(sessionToSave, nextMethods);
  };

  const handleSummarize = async () => {
    if (!session?.id) return;

    if (session.status === SessionStatus.Completed) {
      navigate(`/sessions/${session.id}/distribute`);
      return;
    }

    const saved = await handleSave();
    if (!saved?.id) return;

    navigate(`/sessions/${saved.id}/distribute`);
  };

  const handleSessionInfoUpdate = async (updates: Partial<Session>) => {
    if (!session) return;
    const nextSession: Session = { ...session, ...updates };
    setSession(nextSession);
    await handleSave(nextSession);
  };

  const handleToggleMethod = async (method: Method) => {
    if (!session) return;
    const key = method.id || method.name;
    if (!key) return;
    const current = methodsRef.current;
    const has = current.includes(key) || (method.name && current.includes(method.name));
    const filtered = current.filter((m) => m !== key && m !== method.name);
    const next = has ? filtered : [...filtered, key];
    handleMethodsChange(next);
    await handleSave(session, next);
  };

  const handleCreateMethod = async (values: MethodFormValues) => {
    const creatorEmail = (appContext.state.invoker?.email || "").trim();
    const payload: Method = {
      name: values.name,
      description: values.summary,
      creator: values.creator || creatorEmail || undefined,
    };

    try {
      const saved = await methodService.save(payload);
      setAvailableMethods((prev) => [...prev, saved]);
      setMethodDialogOpen(false);
    } catch (err) {
      console.error("Failed to create method", err);
    }
  };

  const handleTaskCreated = (task: Task) => {
    setTasks((prev) => [
      ...prev,
      {
        ...task,
        actor:
          task.actor ||
          appContext.state.invoker?.email ||
          coacheeEmail ||
          coacheeName ||
          undefined,
        attachments: task.attachments ?? 0,
        creator: task.creator || appContext.state.invoker?.email,
      },
    ]);
    setTaskDialogOpen(false);
  };

  const handleTaskAttachmentsChanged = (
    count: number,
    _meetingId?: string,
    _itemId?: string,
    sessionIdValue?: string,
    taskIdValue?: string,
  ) => {
    if (!session || sessionIdValue !== session.id || !taskIdValue) return;
    setTasks((prev) => prev.map((t) => (t.id === taskIdValue ? { ...t, attachments: count } : t)));
  };

  const handleSessionAttachmentsChanged = (
    count: number,
    _meetingId?: string,
    _itemId?: string,
    sessionIdValue?: string,
  ) => {
    if (!session || sessionIdValue !== session.id) return;
    setSession((prev) => (prev ? { ...prev, attachments: count } : prev));
  };

  const toEditableTaskItem = (task: Task): MeetingItemRepresentation => ({
    id: task.id || "",
    content: task.content || "",
    status:
      (task.status as unknown as MeetingItemStatus) ||
      MeetingItemStatus.NotStarted,
    risk: RiskIndicator.NoRisk,
    task: true,
    decision: false,
    info: false,
    meetingId: undefined,
    meetingName: task.sessionName || session?.name || "",
    sessionId: task.sessionId || session?.id,
    sessionName: task.sessionName || session?.name,
    clientId: task.clientId || session?.clientId,
    isCoachingTask: true,
    creator: task.creator || appContext.state.invoker?.email,
    actor:
      task.actor ||
      appContext.state.invoker?.email ||
      coacheeEmail ||
      coacheeName ||
      undefined,
    category: task.category,
    dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
    overdue: false,
    prioritized: false,
    comments: "",
  });

  const handleSessionTaskUpdated = async (
    itemRepresentation: MeetingItemRepresentation,
  ) => {
    if (!session?.id || !session?.clientId) return;

    const payload: Task = {
      id: itemRepresentation.id,
      clientId: session.clientId,
      sessionId: session.id,
      sessionName: session.name,
      content: (itemRepresentation.content || "").trim(),
      category: itemRepresentation.category || "Coaching Hub",
      actor: itemRepresentation.actor || undefined,
      creator: itemRepresentation.creator || appContext.state.invoker?.email,
      status:
        (itemRepresentation.status as unknown as TaskStatus) ||
        TaskStatus.NotStarted,
      dueDate: itemRepresentation.dueDate
        ? new Date(itemRepresentation.dueDate)
        : undefined,
    };

    try {
      const saved = await taskService.save(payload);
      setTasks((prev) =>
        prev.map((task) =>
          task.id === saved.id
            ? {
                ...task,
                ...saved,
                attachments: saved.attachments ?? task.attachments,
              }
            : task,
        ),
      );
      setEditingTaskItem(undefined);
    } catch (err) {
      console.error("Failed to update session task", err);
    }
  };

  const handleDeleteTask = async (taskId?: string) => {
    if (!taskId) return;
    setTasks((prev) => prev.filter((t) => t.id !== taskId));
    try {
      await taskService.delete(taskId);
    } catch (err) {
      console.error("Failed to delete task", err);
      loadTasks(session?.id || "");
    }
  };

  const renderStatusLabel = (status?: string) => {
    if (!status) return "-";
    const key = status.toLowerCase();
    const translated = t(`item_status.${key}` as const);
    return translated || status;
  };

  return (
    <Box sx={{ minHeight: "100vh", py: 4 }}>
      <Box
        sx={{
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/*TODO: Change the skeleton variant type */}
        {loading && !session && (
          <SkeletonLoader variant="detail" sections={3} />
        )}

        {!loading && !session && (
          <Box
            sx={{
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
              p: 3,
            }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate("/sessions")}
              sx={{ mb: 2 }}
            >
              {t("coachingNavigator.sessionDetails.back")}
            </Button>
            <Typography sx={{
              color: "text.secondary"
            }}>
              {t("coachingNavigator.sessionDetails.notFound")}
            </Typography>
          </Box>
        )}

        {session && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                backgroundColor: "white",
                borderRadius: 2,
                boxShadow: 1,
                px: 3,
                py: 2,
              }}
            >
              <IconButton
                onClick={() => navigate("/sessions")}
                sx={{ color: "text.secondary" }}
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>

              <Typography
                variant="h5"
                sx={{ fontWeight: 600, flexGrow: 1, textAlign: "center" }}
              >
                {t("coachingNavigator.sessionOverviewPage.sessionDetail")}
                {loading && (
                  <CircularProgress
                    size={16}
                    sx={{ ml: 1, verticalAlign: "middle" }}
                  />
                )}
              </Typography>

              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Button
                  variant="outlined"
                  onClick={navigateToPrevious}
                  disabled={!hasPrevious || loading}
                  size="small"
                  startIcon={<ChevronLeftIcon />}
                >
                  {t("coachingNavigator.sessionDetails.previousSession")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={navigateToNext}
                  disabled={!hasNext || loading}
                  size="small"
                  endIcon={<ChevronRightIcon />}
                >
                  {t("coachingNavigator.sessionDetails.nextSession")}
                </Button>
              </Box>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSummarize}
                  disabled={editing || saving}
                  sx={{ textTransform: "none" }}
                >
                  {t("coachingNavigator.sessionDetails.summarize")}
                </Button>
                <Button
                  variant={editing ? "contained" : "outlined"}
                  size="small"
                  startIcon={<EditIcon />}
                  disabled={!isCompleted}
                  onClick={async () => {
                    if (editing) await handleSave();
                    setEditing((prev) => !prev);
                  }}
                  sx={{
                    textTransform: "none",
                    display: "inline-grid",
                    "& .MuiButton-startIcon": {
                      gridArea: "1 / 1",
                      justifySelf: "start",
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-grid",
                      gridArea: "1 / 2",
                      "& > span": { gridArea: "1 / 1" },
                    }}
                  >
                    <span
                      style={{ visibility: editing ? "visible" : "hidden" }}
                    >
                      {t("coachingNavigator.sessionDetails.save")}
                    </span>
                    <span
                      style={{ visibility: editing ? "hidden" : "visible" }}
                    >
                      {t("coachingNavigator.sessionDetails.edit")}
                    </span>
                  </Box>
                </Button>
              </Box>
            </Box>

            <SessionInfoSection
              session={session}
              sessionRemarks={sessionRemarks}
              onSessionRemarksChange={handleSessionRemarksChange}
              onSessionInfoUpdate={handleSessionInfoUpdate}
              onSave={isCompleted ? undefined : handleSave}
              readOnly={readOnly}
            />

            <SessionContentSection
              clientObjective={clientObjective}
              keyInsights={keyInsights}
              sessionNotes={sessionNotes}
              methods={methods}
              availableMethods={availableMethods}
              methodsLoading={methodsLoading}
              onClientObjectiveChange={handleClientObjectiveChange}
              onKeyInsightsChange={handleKeyInsightsChange}
              onSessionNotesChange={handleSessionNotesChange}
              onSave={isCompleted ? undefined : handleSave}
              onToggleMethod={handleToggleMethod}
              onAddMethod={() => setMethodDialogOpen(true)}
              onOpenAttachments={() => setShowSessionAttachments(true)}
              sessionAttachments={session?.attachments || 0}
              readOnly={readOnly}
            />

            <SessionTasksSection
              tasks={tasks}
              tasksLoading={tasksLoading}
              onAddTask={() => {
                setTaskDialogOpen(true);
              }}
              onEditTask={(task) => {
                setEditingTaskItem(toEditableTaskItem(task));
              }}
              onDeleteTask={handleDeleteTask}
              onNavigateToAllTasks={() =>
                navigate(
                  `/dashboard/creator?category=${encodeURIComponent("Coaching Hub")}`,
                )
              }
              onManageAttachments={(task) => setTaskAttachmentsTarget(task)}
              renderStatusLabel={renderStatusLabel}
            />

            <MethodFormDialog
              open={methodDialogOpen}
              onClose={() => setMethodDialogOpen(false)}
              onSubmit={handleCreateMethod}
              title={t("coachingNavigator.methods.dialog.createTitle")}
              submitLabel={t("coachingNavigator.methods.dialog.create")}
              defaultCreatorEmail={appContext.state.invoker?.email}
            />

            <AddTask
              open={taskDialogOpen}
              onClose={() => setTaskDialogOpen(false)}
              onCreated={handleTaskCreated}
              defaultClientId={session.clientId}
              defaultSessionId={session.id}
              defaultSessionName={session.name}
              defaultActorName={
                appContext.state.invoker?.email ||
                coacheeEmail ||
                coacheeName ||
                undefined
              }
              defaultCreatorEmail={appContext.state.invoker?.email}
            />

            {showSessionAttachments && session && (
              <AttachmentsModal
                open={true}
                allowModifications={!readOnly}
                height="70vh"
                uploadPrefix={`sessions/${session.id}/`}
                sessionId={session.id}
                onClose={() => setShowSessionAttachments(false)}
                onAttachmentsCountChanged={handleSessionAttachmentsChanged}
              />
            )}

            {taskAttachmentsTarget && taskAttachmentsTarget.id && session && (
              <AttachmentsModal
                open={true}
                allowModifications={!readOnly}
                height="70vh"
                uploadPrefix={`sessions/${session.id}/tasks/${taskAttachmentsTarget.id}/`}
                sessionId={session.id}
                taskId={taskAttachmentsTarget.id}
                onClose={() => setTaskAttachmentsTarget(null)}
                onAttachmentsCountChanged={handleTaskAttachmentsChanged}
              />
            )}

            {editingTaskItem && (
              <ItemEditingPanelSimple
                meetingItem={editingTaskItem}
                open={true}
                allowMetadataEditing={true}
                allowAttachmentsEditing={true}
                onClose={() => setEditingTaskItem(undefined)}
                onUpdate={handleSessionTaskUpdated}
                onAttachmentsCountChanged={handleTaskAttachmentsChanged}
              />
            )}

            {/* TODO: Add delete confirmation dialog */}
          </>
        )}
      </Box>
    </Box>
  );
};

export default SessionDetails;
